import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Instagram, Facebook } from "lucide-react";
import { NAV, CONTACT, SOCIALS } from "../../data/site";
import { useEstimate } from "./EstimateDialog";
import { GoogleG } from "./icons/GoogleG";

const SOCIAL_ICON = { Google: GoogleG, Instagram, Facebook };

export const Logo = ({ full = false }) => (
  <a href="#home" data-testid="logo" className="inline-flex shrink-0 items-center">
    <span className="flex items-center rounded-xl bg-white px-3 py-1.5 shadow-sm ring-1 ring-black/5">
      <img
        src={full ? "/fastfix-logo.png" : "/fastfix-logo-mark.png"}
        alt="Fast Fix Charlotte LLC — Residential & Commercial Property Solutions"
        className={full ? "h-16 w-auto" : "h-9 w-auto"}
      />
    </span>
  </a>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { open } = useEstimate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || menu;

  return (
    <>
      <motion.header
        initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 lg:px-10">
          <Logo />

          <div className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`nav-link text-sm font-semibold transition-colors ${
                  solid ? "text-ffc-ink hover:text-ffc-blue" : "text-white/90 hover:text-white"
                }`}>
                {n.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-1 md:flex">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICON[s.icon];
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`nav-social-${s.name.toLowerCase()}`}
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                      solid
                        ? "text-ffc-ink hover:bg-ffc-blue hover:text-white"
                        : "text-white/90 hover:bg-white/15 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
            <a href={CONTACT.phoneHref} data-testid="nav-call"
              className={`hidden items-center gap-2 text-sm font-bold md:flex ${
                solid ? "text-ffc-blue" : "text-white"
              }`}>
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <a
              href={CONTACT.phoneHref}
              aria-label={`Call ${CONTACT.phone}`}
              data-testid="nav-call-mobile"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors md:hidden ${
                solid
                  ? "text-ffc-blue hover:bg-ffc-blue hover:text-white"
                  : "text-white hover:bg-white/15"
              }`}
            >
              <Phone className="h-5 w-5" />
            </a>
            <button onClick={open} data-testid="nav-estimate-btn"
              className="btn-red hidden rounded-full px-5 py-2.5 text-sm font-semibold sm:inline-flex">
              Get Free Estimate
            </button>
            <button onClick={() => setMenu((m) => !m)} data-testid="mobile-menu-toggle"
              className={`lg:hidden ${solid ? "text-ffc-ink" : "text-white"}`} aria-label="Menu">
              {menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white pt-24 lg:hidden" data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6">
              {NAV.map((n, i) => (
                <motion.a key={n.href} href={n.href} onClick={() => setMenu(false)}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-display border-b border-ffc-line py-4 text-2xl">
                  {n.label}
                </motion.a>
              ))}
              <button onClick={() => { setMenu(false); open(); }}
                className="btn-red mt-6 rounded-full py-4 text-base font-semibold">
                Get Free Estimate
              </button>
              <a href={CONTACT.phoneHref} className="mt-4 flex items-center justify-center gap-2 font-bold text-ffc-blue">
                <Phone className="h-4 w-4" /> {CONTACT.phone}
              </a>
              <div className="mt-5 flex items-center justify-center gap-3">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICON[s.icon];
                  return (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      data-testid={`mobile-social-${s.name.toLowerCase()}`}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-ffc-line text-ffc-ink transition-colors hover:border-ffc-blue hover:bg-ffc-blue hover:text-white"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
